import React from "react";
import {
    Chart as ChartJS,
    ChartData,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Filler,
    Title,
    Tooltip,
    Legend,
} from "chart.js";

import ChartDataLabels from "chartjs-plugin-datalabels";
import { Line, Bar } from "react-chartjs-2";
import { RootState } from "../../store";
import { connect } from "react-redux";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Filler,
    Title,
    Tooltip,
    Legend,
    ChartDataLabels
);

// ============================================================
// INTERFACES
// ============================================================

export interface EvolucionInventarioItem {
    meses: string;
    valorContable: number;
}

export interface DepreciacionInventarioItem {
    mes: number;
    nombreMes: string;

    // Bienes que sí cumplen las condiciones de depreciación
    cantidadBienes: number;

    // Bienes que NO cumplen las condiciones de depreciación
    cantidadBienesNoDepreciables: number;

    valorCompra: number;
    valorDepreciado: number;
    depreciacionAcumulada: number;
}

export interface Props {
    data?: EvolucionInventarioItem[];
    depreciacionMensual: DepreciacionInventarioItem[];
    title?: string;
    isDarkMode: boolean;
}

// ============================================================
// COMPONENTE
// ============================================================

const ValorContableChart: React.FC<Props> = ({
    depreciacionMensual,
    title = "Evolución del Valor de Inventario",
    isDarkMode
}) => {

    // ============================================================
    // MESES
    // ============================================================

    const meses = [
        "Ene",
        "Feb",
        "Mar",
        "Abr",
        "May",
        "Jun",
        "Jul",
        "Ago",
        "Sep",
        "Oct",
        "Nov",
        "Dic",
    ];

    // ============================================================
    // OBTENER DATO DEL MES
    // ============================================================

    const obtenerDatoMes = (mesNumero: number) => {
        return depreciacionMensual.find(
            (x) => Number(x.mes) === mesNumero
        );
    };

    // ============================================================
    // VALORES MONETARIOS
    // ============================================================

    const valoresCompra = meses.map((_, index) => {
        const item = obtenerDatoMes(index + 1);

        return item?.valorCompra ?? 0;
    });

    const valoresDepreciacionAcumulada = meses.map((_, index) => {
        const item = obtenerDatoMes(index + 1);

        return item?.depreciacionAcumulada ?? 0;
    });

    const valoresContables = meses.map((_, index) => {
        const item = obtenerDatoMes(index + 1);

        return item?.valorDepreciado ?? 0;
    });

    // ============================================================
    // CANTIDAD DE BIENES DEPRECIABLES
    // ============================================================

    const cantidadBienes = meses.map((_, index) => {
        const item = obtenerDatoMes(index + 1);

        return item?.cantidadBienes ?? 0;
    });

    // ============================================================
    // CANTIDAD DE BIENES NO DEPRECIABLES
    // ============================================================

    // const cantidadBienesNoDepreciables = meses.map((_, index) => {
    //     const item = obtenerDatoMes(index + 1);

    //     return item?.cantidadBienesNoDepreciables ?? 0;
    // });

    // ============================================================
    // FORMATEAR MONEDA
    // ============================================================

    const formatearMoneda = (valor: number) => {
        return `$${valor.toLocaleString("es-CL", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        })}`;
    };

    // ============================================================
    // FORMATEAR MONEDA COMPACTA
    // ============================================================

    const formatearCompacto = (valor: number) => {

        if (valor >= 1_000_000_000) {
            return `$${(valor / 1_000_000_000).toFixed(1)}B`;
        }

        if (valor >= 1_000_000) {
            return `$${(valor / 1_000_000).toFixed(1)}M`;
        }

        if (valor >= 1_000) {
            return `$${(valor / 1_000).toFixed(0)}K`;
        }

        return `$${valor.toLocaleString("es-CL")}`;
    };

    // ============================================================
    // FORMATEAR CANTIDAD
    // ============================================================

    const formatearCantidad = (valor: number) => {
        return valor.toLocaleString("es-CL", {
            maximumFractionDigits: 0,
        });
    };

    // ============================================================
    // GRÁFICO PRINCIPAL
    // VALORES MONETARIOS
    // ============================================================

    const chartData: ChartData<"line", number[], string> = {

        labels: meses,

        datasets: [

            // ----------------------------------------------------
            // VALOR DE COMPRA
            // ----------------------------------------------------

            {
                label: "Valor de Compra",

                data: valoresCompra,

                borderColor: "#0d6efd",

                backgroundColor:
                    "rgba(13, 110, 253, 0.12)",

                borderWidth: 2.5,

                pointRadius: 4,

                pointHoverRadius: 7,

                pointBorderWidth: 2,

                pointBackgroundColor: "#ffffff",

                pointBorderColor: "#0d6efd",

                tension: 0.3,

                fill: true,

                datalabels: {
                    display: false,
                },
            },

            // ----------------------------------------------------
            // DEPRECIACIÓN ACUMULADA
            // ----------------------------------------------------

            {
                label: "Depreciación Acumulada",

                data: valoresDepreciacionAcumulada,

                borderColor: "#f0ad00",

                backgroundColor:
                    "rgba(255, 193, 7, 0.18)",

                borderWidth: 2,

                pointRadius: 4,

                pointHoverRadius: 7,

                pointBorderWidth: 2,

                pointBackgroundColor: "#ffffff",

                pointBorderColor: "#f0ad00",

                tension: 0.3,

                fill: true,

                datalabels: {

                    display: true,

                    align: "top",

                    anchor: "end",

                    offset: 4,

                    color: "#b47d00",

                    font: {
                        size: 9,
                        weight: "bold",
                    },

                    formatter: (value: number) =>
                        formatearMoneda(value),
                },
            },

            // ----------------------------------------------------
            // VALOR CONTABLE
            // ----------------------------------------------------

            {
                label: "Valor Contable",

                data: valoresContables,

                borderColor: "#198754",

                backgroundColor:
                    "rgba(25, 135, 84, 0.10)",

                borderWidth: 2.5,

                pointRadius: 4,

                pointHoverRadius: 7,

                pointBorderWidth: 2,

                pointBackgroundColor: "#ffffff",

                pointBorderColor: "#198754",

                tension: 0.3,

                fill: true,

                datalabels: {
                    display: false,
                },
            },
        ],
    };

    // ============================================================
    // OPCIONES GRÁFICO PRINCIPAL
    // ============================================================

    const options = {

        responsive: true,

        maintainAspectRatio: false,

        interaction: {
            mode: "index" as const,
            intersect: false,
        },

        plugins: {

            title: {

                display: false,

                text: title,

                font: {
                    size: 16,
                    weight: "bold" as const,
                },
            },

            legend: {

                display: true,

                position: "top" as const,

                labels: {

                    usePointStyle: true,

                    pointStyle: "circle",

                    padding: 14,

                    font: {
                        size: 11,
                    },
                },
            },

            tooltip: {

                enabled: true,

                backgroundColor:
                    "rgba(40, 40, 40, 0.88)",

                titleColor: "#ffffff",

                bodyColor: "#ffffff",

                borderColor:
                    "rgba(255,255,255,0.15)",

                borderWidth: 1,

                padding: 10,

                displayColors: true,

                callbacks: {

                    title: (tooltipItems: any[]) => {

                        return tooltipItems[0]?.label ?? "";

                    },

                    label: (context: any) => {

                        const value =
                            context.parsed.y ?? 0;

                        return ` ${context.dataset.label}: ${formatearMoneda(value)}`;
                    },
                },
            },

            datalabels: {

                clamp: true,

                clip: false,
            },
        },

        scales: {

            x: {

                grid: {
                    color: isDarkMode ? "#ffffff2a" : "#30353a1a",
                },

                ticks: {
                    font: {
                        size: 11,
                    },
                    color: isDarkMode ? "#ffffffa9" : "#30353ac4",
                },
            },

            y: {

                beginAtZero: true,

                grid: {
                    color: isDarkMode ? "#ffffff2a" : "#30353a1a",
                },

                ticks: {
                    font: {
                        size: 10,
                    },
                    color: isDarkMode ? "#ffffffa9" : "#30353ac4",
                    callback: function (value: any) {

                        return formatearCompacto(
                            Number(value)
                        );
                    },
                },
            },
        },
    };

    // ============================================================
    // GRÁFICO DE CANTIDAD DE BIENES
    // ============================================================

    const cantidadBienesData: ChartData<
        "bar",
        number[],
        string
    > = {

        labels: meses,
        datasets: [

            // BIENES DEPRECIABLES
            {
                label: "Bienes depreciables",
                data: cantidadBienes,
                backgroundColor: "rgba(25, 135, 84, 0.70)",
                borderColor: "#198754",
                borderWidth: 1,
                borderRadius: 5,
                borderSkipped: false,
                maxBarThickness: 40,
                datalabels: {
                    display: true,
                    anchor: "end",
                    align: "top",
                    offset: 3,
                    color: "#198754",
                    font: {
                        size: 11,
                        weight: "bold",
                    },

                    formatter: (value: number) =>
                        formatearCantidad(value),
                },
            },

            // ====================================================
            // BIENES NO DEPRECIABLES
            // ====================================================

            // {
            //     label: "Bienes no depreciados",

            //     data: cantidadBienesNoDepreciables,

            //     backgroundColor:
            //         "rgba(220, 53, 69, 0.65)",

            //     borderColor: "#dc3545",

            //     borderWidth: 1,

            //     borderRadius: 5,

            //     borderSkipped: false,

            //     maxBarThickness: 35,

            //     datalabels: {

            //         display: true,

            //         anchor: "end",

            //         align: "top",

            //         offset: 3,

            //         color: "#dc3545",

            //         font: {
            //             size: 9,
            //             weight: "bold",
            //         },

            //         formatter: (value: number) =>
            //             formatearCantidad(value),
            //     },
            // },
        ],
    };

    // ============================================================
    // OPCIONES GRÁFICO DE CANTIDADES
    // ============================================================

    const cantidadBienesOptions = {

        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: "index" as const,
            intersect: false,
        },

        plugins: {
            legend: {
                display: false,
                position: "top" as const,
                labels: {
                    usePointStyle: true,
                    pointStyle: "rectRounded",
                    padding: 15,
                    font: {
                        size: 11,
                    },
                },
            },

            tooltip: {

                enabled: true,
                backgroundColor: "rgba(40, 40, 40, 0.90)",
                titleColor: "#ffffff",
                bodyColor: "#ffffff",
                padding: 10,
                displayColors: true,
                callbacks: {
                    title: (tooltipItems: any[]) => {
                        return tooltipItems[0]?.label ?? "";
                    },

                    label: (context: any) => {
                        const value = context.parsed.y ?? 0;
                        return ` ${context.dataset.label}: ${formatearCantidad(value)} bienes`;
                    },
                },
            },

            datalabels: {
                clamp: true,
                clip: false,
            },
        },

        scales: {

            x: {
                stacked: false,
                grid: {
                    display: true,
                    color: isDarkMode ? "#ffffff2a" : "#30353a1a",
                },
                ticks: {
                    font: {
                        size: 11,
                    },
                    color: isDarkMode ? "#ffffffa9" : "#30353ac4",
                },
            },

            y: {

                beginAtZero: true,
                stacked: false,
                grid: {
                    display: true,
                    color: isDarkMode ? "#ffffff2a" : "#30353a1a",
                    font: {
                        size: 11,
                    },
                },
                ticks: {
                    precision: 0,
                    font: {
                        size: 10,
                    },
                    color: isDarkMode ? "#ffffffa9" : "#30353ac4",
                    callback: function (value: any) {
                        return formatearCantidad(
                            Number(value)
                        );
                    },
                },

            },
        },
    };

    return (

        <div
            style={{
                width: "100%",
            }}
        >

            {/* GRÁFICO DE VALORES MONETARIOS */}
            <div
                style={{
                    width: "100%",
                    height: "320px",
                    position: "relative",
                }}
            >

                <Line
                    data={chartData}
                    options={options}
                />

            </div>

            {/* GRÁFICO DE CANTIDAD DE BIENES */}
            <div
                style={{
                    marginTop: "30px",
                    paddingTop: "20px",
                    borderTop:
                        "1px solid rgba(0,0,0,0.08)",
                }}
            >

                <div
                    style={{
                        fontSize: "14px",
                        fontWeight: 600,
                        marginBottom: "10px",
                        // color: "#444",
                    }}
                >
                    CANTIDAD DE BIENES DEPRECIADOS
                </div>


                <div
                    style={{
                        width: "100%",
                        height: "200px",
                        position: "relative",
                    }}
                >

                    <Bar
                        data={cantidadBienesData}
                        options={cantidadBienesOptions}
                    />

                </div>

            </div>

        </div>
    );
};


const mapStateToProps = (state: RootState) => ({
    isDarkMode: state.darkModeReducer.isDarkMode,
});
export default connect(mapStateToProps, {
}
)(ValorContableChart);